import { useCallback } from 'react';

import type { TablePaginationConfig, TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useShallow } from 'zustand/shallow';

import type { DagTableRow } from './types';
import { useDagListDashboardStore } from './useDagListDashboardStore';

const TABLE_SORT_FIELD_MAP = {
  name: 'name',
  nextRun: 'next_run',
  lastRun: 'last_run',
} as const;

export const useDagTablePagination = () => {
  const { currentPage, pageSize, sortBy, sortOrder, setSort, setPage, resetPage } = useDagListDashboardStore(
    useShallow((state) => ({
      currentPage: state.currentPage,
      pageSize: state.pageSize,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      setSort: state.setSort,
      setPage: state.setPage,
      resetPage: state.resetPage,
    }))
  );

  const handlePaginationChange = useCallback(
    (page: number, nextPageSize: number) => {
      setPage(page, nextPageSize);
    },
    [setPage]
  );

  const handleTableChange: TableProps<DagTableRow>['onChange'] = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter: SorterResult<DagTableRow> | SorterResult<DagTableRow>[]
    ) => {
      const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const columnKey = singleSorter.columnKey ? String(singleSorter.columnKey) : '';
      const nextSortBy = singleSorter.order
        ? TABLE_SORT_FIELD_MAP[columnKey as keyof typeof TABLE_SORT_FIELD_MAP]
        : undefined;
      const nextSortOrder = singleSorter.order ? (singleSorter.order === 'ascend' ? 'asc' : 'desc') : undefined;

      setSort(nextSortBy, nextSortOrder);
    },
    [setSort]
  );

  return {
    currentPage,
    currentPageSize: pageSize,
    sortBy,
    sortOrder,
    resetToFirstPage: resetPage,
    handleTableChange,
    handlePaginationChange,
    setCurrentPage: (page: number) => setPage(page),
  } as const;
};
